import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
    Container,
    Typography,
    Box,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Button,
    Chip,
    TextField,
    CircularProgress,
    Divider,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    Tab,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Link,
    Collapse,
    Autocomplete,
    useMediaQuery,
    Switch,
} from "@mui/material";
import { TabContext, TabList } from "@mui/lab";
import { CheckCircle, Cancel, Refresh, Edit, TableChart, ExpandMore, ExpandLess, Add as AddIcon, Download, ContentPaste, HowToReg, Block, PictureAsPdf } from "@mui/icons-material";
import styled from "@emotion/styled";
import { getQuotes, getQuote, updateQuote, approveQuote, rejectQuote, acceptQuote, declineQuote, previewQuotePdf, reopenQuote, cloneQuote, setCurrentQuote, setCurrentQuoteComments } from "../redux/slices/quote";
import { fetchAccountUsers } from "../redux/slices/users";
import { regenerateQuote } from "../redux/ducks/ai";
import { createQuoteTemplateFromQuote, getTemplates } from "../redux/ducks/template";
import SaveQuoteAsTemplateDialog from "../components/templates/SaveQuoteAsTemplateDialog";
import QuoteTemplatePicker from "../components/templates/QuoteTemplatePicker";
import MarkdownEditor from '../components/MarkdownEditor';
import MarkdownDisplay from '../components/MarkdownDisplay';
import AppDatePicker from '../components/AppDatePicker';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import QuoteRequestDialog from '../components/QuoteRequestDialog';
import CommentThread from '../components/CommentThread';
import PageHeader from '../components/PageHeader';
import { showAlert } from "../redux/slices/alerts";
import { useHasPermission } from "../utils/permissionUtils";
import { getDepartments } from "../redux/ducks/department";
import { getSkills } from "../redux/slices/skill";
import { selectableSkills, skillOptionLabel } from "../utils/skillUtils";
import { getSocket } from "../services/socketService";
import { statusColor } from "../utils/statusColors";
import { formatDate as formatDay, formatDateTime } from "../utils/dateUtils";

const PageContainer = styled(Container)(({ theme }) => ({
    padding: theme.spacing(2),
    width: "100%",
    minWidth: 0,
    overflowX: "clip",
}));

const ScopeList = styled("ul")(({ theme }) => ({
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    "& li": {
        marginBottom: theme.spacing(0.5),
    },
}));

const ScopeItemRow = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: "flex-start",
}));

const LineItemsTableWrapper = styled(Box)({
    overflowX: 'auto',
    width: '100%',
});

const QuoteRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== "$selected",
})(({ theme, $selected }) => ({
    cursor: "pointer",
    backgroundColor: $selected ? theme.palette.action.selected : "inherit",
    "&:hover": {
        backgroundColor: $selected ? theme.palette.action.selected : theme.palette.action.hover,
    },
}));

const StatusChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== "$status",
})(({ theme, $status }) => {
    const bg = statusColor(theme, "quote", $status);
    return {
        backgroundColor: bg,
        color: theme.palette.getContrastText(bg),
        fontWeight: theme.typography.fontWeightSemiBold,
        borderRadius: "16px",
    };
});

const DetailPanel = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 3, 3),
    [theme.breakpoints.down("md")]: {
        padding: theme.spacing(2, 0, 0),
    },
}));

const DetailExpansionCell = styled(TableCell)(({ theme }) => ({
    padding: 0,
    borderBottom: 0,
    backgroundColor: theme.palette.surface.inset,
}));

const DetailLoadingBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(3),
}));

const MobileDetailPanel = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1.5),
    cursor: "default",
}));

const LineItemRow = styled(TableRow)({
    cursor: "default",
    "&:hover": {
        backgroundColor: "inherit",
    },
});

const DetailHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
}));

const DetailDivider = styled(Divider)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ProjectLinkNotice = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const DepositInvoiceNotice = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const OnBehalfNotice = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const MetaGrid = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr 1fr",
    },
}));

const DescriptionBox = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const NotifyBox = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));


const LineItemsTable = styled(Table)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const TitleHeaderCell = styled(TableCell)({
    width: "24%",
});

const DescriptionHeaderCell = styled(TableCell)({
    width: "36%",
});

const LineItemsHelperText = styled(Typography)(({ theme }) => ({
    display: "block",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
}));

const ChevronCell = styled(TableCell)({
    width: 28,
    padding: '0 4px',
});

const SectionLabel = styled(Typography)(({ theme }) => ({
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeightSemiBold,
    letterSpacing: 1,
    display: 'block',
    marginBottom: theme.spacing(0.5),
}));

const SectionLabelTight = styled(Typography)(({ theme }) => ({
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeightSemiBold,
    letterSpacing: 1,
    display: 'block',
    marginBottom: theme.spacing(1),
}));

const DialogHelperText = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
}));

const ApplyTemplateWarning = styled(Alert)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const HoursField = styled(TextField)({
    width: 90,
});


const RushRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
}));

const ActionButtonsRow = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
}));

const ContingencyRow = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
}));

const DiscountRow = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginTop: theme.spacing(1),
}));

const DiscountFormControl = styled(FormControl)({
    minWidth: 120,
});

const DiscountValueField = styled(TextField)({
    width: 120,
});

const PriceSummaryBox = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
}));

const PriceSummaryRow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const PaymentEditorBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const PaymentRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(1),
}));

const NarrowField = styled(TextField)({
    width: 80,
});

const TabsWrapper = styled(Box)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
}));

const PaginationBox = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(3),
}));

const TableWrapper = styled(Box)(({ theme }) => ({
    [theme.breakpoints.down("md")]: {
        display: "none",
    },
}));

const MobileCardList = styled(Box)(({ theme }) => ({
    display: "none",
    [theme.breakpoints.down("md")]: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
}));

const QuoteCard = styled(Paper, {
    shouldForwardProp: (prop) => prop !== "$selected",
})(({ theme, $selected }) => ({
    padding: theme.spacing(1.5),
    cursor: "pointer",
    borderLeft: $selected
        ? `4px solid ${theme.palette.primary.main}`
        : "4px solid transparent",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
}));

const Quotes = () => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const quotes = useSelector((state) => state.quote.quotes);
    const currentQuote = useSelector((state) => state.quote.currentQuote);
    const skills = useSelector((state) => state.skills || []);
    const accountUsersAccountId = useSelector((state) => state.accountUsers.accountId);

    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

    const navigate = useNavigate();
    const { quoteId: routeQuoteId } = useParams();
    const deepLinkedQuoteRef = useRef(null);
    const [selectedQuoteId, setSelectedQuoteId] = useState(routeQuoteId || null);
    const selectedQuoteIdRef = useRef(selectedQuoteId);
    selectedQuoteIdRef.current = selectedQuoteId;
    useEffect(() => {
        if (routeQuoteId) setSelectedQuoteId(routeQuoteId);
    }, [routeQuoteId]);
    const [newQuoteOpen, setNewQuoteOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editedLineItems, setEditedLineItems] = useState([]);
    const [isActioning, setIsActioning] = useState({ approve: false, reject: false, regenerate: false, applyTemplate: false, accept: false, decline: false, previewPdf: false, reopen: false, clone: false });
    const [onBehalfAction, setOnBehalfAction] = useState(null);
    const [onBehalfNote, setOnBehalfNote] = useState('');
    const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
    const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
    const [regenerateInput, setRegenerateInput] = useState('');
    const [departments, setDepartments] = useState([]);
    const [editedName, setEditedName] = useState('');
    const [editedNotes, setEditedNotes] = useState('');
    const [editedManagerNotes, setEditedManagerNotes] = useState('');
    const [editedAssumptions, setEditedAssumptions] = useState([]);
    const [editedInclusions, setEditedInclusions] = useState([]);
    const [editedExclusions, setEditedExclusions] = useState([]);
    const [editedDueDate, setEditedDueDate] = useState('');
    const [editedDepartment, setEditedDepartment] = useState('');
    const [editedSkill, setEditedSkill] = useState('');
    const [editedBillingType, setEditedBillingType] = useState('Fixed Price');
    const [editedDiscountType, setEditedDiscountType] = useState('');
    const [editedDiscountValue, setEditedDiscountValue] = useState(0);
    const [editedDescription, setEditedDescription] = useState('');
    const [editedPayments, setEditedPayments] = useState([]);
    const [editedExtraDays, setEditedExtraDays] = useState(0);
    const [editedDaysBeforeStart, setEditedDaysBeforeStart] = useState(0);
    const [editedLockedRate, setEditedLockedRate] = useState('');
    const [editedNotifyUsers, setEditedNotifyUsers] = useState([]);
    const [editedRush, setEditedRush] = useState(false);
    const [localContingencyHours, setLocalContingencyHours] = useState(null);
    const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);
    const [applyTemplateId, setApplyTemplateId] = useState('');
    const [quoteTemplates, setQuoteTemplates] = useState([]);

    const [tabValue, setTabValue] = useState('active');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;

    const TAB_GROUPS = {
        active: ['Pending', 'Draft', 'Under Review'],
        sent: ['Sent'],
        approved: ['Accepted'],
        rejected: ['Declined', 'Rejected'],
        all: ['Pending', 'Draft', 'Under Review', 'Sent', 'Accepted', 'Declined', 'Rejected'],
    };

    const handleTabChange = (_event, newValue) => {
        setTabValue(newValue);
        setCurrentPage(1);
        setSelectedQuoteId(null);
    };

    const tabBadgeLabel = (label, tab) => {
        const count = quotes.filter((q) => TAB_GROUPS[tab].includes(q.status)).length;
        return count > 0 ? `${label} (${count})` : label;
    };

    const filteredByTab = quotes.filter((q) => TAB_GROUPS[tabValue].includes(q.status));
    const totalPages = Math.ceil(filteredByTab.length / itemsPerPage);
    const pagedQuotes = filteredByTab.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    useEffect(() => {
        dispatch(getQuotes({}));
        dispatch(getSkills());
        dispatch(getDepartments(0, 1000, (data) => setDepartments(data || [])));
        dispatch(getTemplates("quote", 0, 200, (data) => setQuoteTemplates(data || [])));
    }, [dispatch]);

    useEffect(() => {
        if (selectedQuoteId) {
            dispatch(getQuote({ quoteId: selectedQuoteId }));
            setEditMode(false);
        }
    }, [selectedQuoteId, dispatch]);

    // The detail expands inside its own row, so a deep link only shows up once
    // its row is on screen — move the tab and page to wherever that quote lives.
    useEffect(() => {
        if (!routeQuoteId || quotes.length === 0 || deepLinkedQuoteRef.current === routeQuoteId) return;
        const target = quotes.find((q) => q._id === routeQuoteId);
        if (!target) return;
        deepLinkedQuoteRef.current = routeQuoteId;
        const tab = TAB_GROUPS[tabValue].includes(target.status) ? tabValue : 'all';
        const position = quotes.filter((q) => TAB_GROUPS[tab].includes(q.status)).indexOf(target);
        setTabValue(tab);
        setCurrentPage(Math.floor(position / itemsPerPage) + 1);
    }, [routeQuoteId, quotes]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;
        const handler = (payload) => {
            dispatch(getQuotes({}));
            if (payload?.quoteId && payload.quoteId === selectedQuoteId) {
                dispatch(getQuote({ quoteId: selectedQuoteId }));
            }
        };
        socket.on('quoteAi:ready', handler);
        return () => { socket.off('quoteAi:ready', handler); };
    }, [dispatch, selectedQuoteId]);

    const quoteAccountId = currentQuote && currentQuote._id === selectedQuoteId ? currentQuote.account?._id || currentQuote.account || null : null;
    useEffect(() => {
        if (quoteAccountId && quoteAccountId !== accountUsersAccountId) {
            dispatch(fetchAccountUsers({ accountId: quoteAccountId }));
        }
    }, [quoteAccountId, accountUsersAccountId, dispatch]);

    if (!hasPermission("canManageQuotes")) {
        return (
            <Container>
                <Typography>You do not have permission to view this page.</Typography>
            </Container>
        );
    }

    const handleRowClick = (quoteId) => {
        if (selectedQuoteId === quoteId) {
            setSelectedQuoteId(null);
        } else {
            setSelectedQuoteId(quoteId);
        }
    };

    const handleEditLineItems = () => startEditing(currentQuote);

    const startEditing = (quote) => {
        setEditedLineItems([...(quote.lineItems || []).map((item) => ({
            ...item,
            title: item.title || '',
            description: item.description || '',
            workType: item.workType?._id || item.workType || null,
        }))]);
        setEditedName(quote.name || '');
        setEditedNotes(quote.notes || '');
        setEditedManagerNotes(quote.managerNotes || '');
        setEditedAssumptions([...(quote.assumptions || [])]);
        setEditedInclusions([...(quote.inclusions || [])]);
        setEditedExclusions([...(quote.exclusions || [])]);
        setEditedDueDate(quote.dueDate ? quote.dueDate.split('T')[0] : '');
        setEditedDepartment(quote.department?._id || quote.department || '');
        setEditedSkill(quote.skill?._id || quote.skill || '');
        setEditedBillingType(quote.billingType || 'Fixed Price');
        setEditedDiscountType(quote.discountType || '');
        setEditedDiscountValue(quote.discountValue || 0);
        setEditedDescription(quote.description || '');
        setEditedPayments(
            quote.payments && quote.payments.length > 0
                ? quote.payments.map((p) => ({ ...p }))
                : [
                      { paymentNumber: 1, percentage: 50, date: '', status: 'Pending' },
                      { paymentNumber: 2, percentage: 50, date: '', status: 'Pending' },
                  ]
        );
        setEditedExtraDays(quote.extraDays || 0);
        setEditedDaysBeforeStart(quote.daysBeforeStart || 0);
        setEditedLockedRate(quote.lockedRate != null ? String(quote.lockedRate) : '');
        setEditedRush(!!quote.rush);
        const eligibleIds = quote.eligibleNotifyUsers ? new Set(quote.eligibleNotifyUsers.map((u) => String(u._id))) : null;
        setEditedNotifyUsers((quote.notifyUsers || []).filter((u) => !eligibleIds || eligibleIds.has(String(u._id || u))));
        setEditMode(true);
    };

    const handleLineItemActionableChange = (index, checked) => {
        setEditedLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, isActionable: checked } : item)));
    };

    const handleCancelEdit = () => {
        setEditMode(false);
        setLocalContingencyHours(null);
    };

    const handleSaveChanges = () => {
        if (!lockedRateValid) return;
        dispatch(
            updateQuote({
                quoteId: selectedQuoteId,
                data: {
                    lineItems: editedLineItems,
                    name: editedName,
                    notes: editedNotes,
                    managerNotes: editedManagerNotes,
                    assumptions: editedAssumptions,
                    inclusions: editedInclusions,
                    exclusions: editedExclusions,
                    dueDate: editedDueDate || null,
                    department: editedDepartment || null,
                    skill: editedSkill || null,
                    billingType: editedBillingType,
                    discountType: editedDiscountType || null,
                    discountValue: editedDiscountValue,
                    description: editedDescription,
                    payments: editedPayments,
                    extraDays: editedExtraDays,
                    daysBeforeStart: editedDaysBeforeStart,
                    lockedRate: Number(editedLockedRate),
                    notifyUsers: editedNotifyUsers.map((u) => u._id),
                    rush: editedRush,
                },
                callback: () => {
                    dispatch(getQuotes({}));
                    dispatch(getQuote({ quoteId: selectedQuoteId }));
                    setEditMode(false);
                    setLocalContingencyHours(null);
                    dispatch(showAlert({ severity: "success", message: "Quote updated" }));
                },
            })
        );
    };

    const recomputeContingency = (items) => {
        const rate = currentQuote?.contingencyRate ?? 0.10;
        const newSubtotal = items.reduce((sum, item) => sum + (parseFloat(item.hours) || 0), 0);
        setLocalContingencyHours(Math.round(newSubtotal * rate));
    };

    const handleLineItemHoursChange = (index, value) => {
        const updated = editedLineItems.map((item, i) => (i === index ? { ...item, hours: parseFloat(value) || 0 } : item));
        setEditedLineItems(updated);
        recomputeContingency(updated);
    };

    const handleAddLineItem = () => {
        const next = [...editedLineItems, { title: '', description: '', workType: editedSkill || null, hours: 0, isActionable: false }];
        setEditedLineItems(next);
        recomputeContingency(next);
    };

    const handleDeleteLineItem = (index) => {
        const next = editedLineItems.filter((_, i) => i !== index);
        setEditedLineItems(next);
        recomputeContingency(next);
    };

    const handleLineItemWorkTypeChange = (index, value) => {
        setEditedLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, workType: value || null } : item)));
    };

    const handleLineItemTitleChange = (index, value) => {
        setEditedLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, title: value } : item)));
    };

    const handleLineItemDescriptionChange = (index, value) => {
        setEditedLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, description: value } : item)));
    };

    const renderScopeList = (label, helper, items, setItems) => (
        <>
            <SectionLabel variant="caption" color="text.secondary">{label}</SectionLabel>
            <Box mb={2}>
                {editMode ? (
                    <>
                        {items.length === 0 && (
                            <Typography variant="body2" color="text.secondary" mb={1}>{helper}</Typography>
                        )}
                        {items.map((text, i) => (
                            <ScopeItemRow key={i}>
                                <TextField
                                    fullWidth
                                    multiline
                                    size="small"
                                    value={text}
                                    onChange={(e) => setItems(items.map((t, j) => (j === i ? e.target.value : t)))}
                                />
                                <Button size="small" color="error" onClick={() => setItems(items.filter((_, j) => j !== i))}>
                                    Remove
                                </Button>
                            </ScopeItemRow>
                        ))}
                        <Button size="small" startIcon={<AddIcon />} onClick={() => setItems([...items, ''])}>
                            Add item
                        </Button>
                    </>
                ) : (Array.isArray(items) && items.length > 0
                    ? <ScopeList>{items.map((text, i) => <li key={i}><Typography variant="body2" component="span">{text}</Typography></li>)}</ScopeList>
                    : <Typography variant="body2">—</Typography>
                )}
            </Box>
        </>
    );

    const handleApprove = () => {
        setIsActioning((prev) => ({ ...prev, approve: true }));
        dispatch(
            approveQuote({
                quoteId: selectedQuoteId,
                callback: (data) => {
                    setIsActioning((prev) => ({ ...prev, approve: false }));
                    if (!data) return;
                    dispatch(getQuotes({}));
                    dispatch(getQuote({ quoteId: selectedQuoteId }));
                    dispatch(showAlert({ severity: "success", message: "Quote approved and sent to client" }));
                },
            })
        );
    };

    const handleReject = () => {
        setIsActioning((prev) => ({ ...prev, reject: true }));
        dispatch(
            rejectQuote({
                quoteId: selectedQuoteId,
                callback: () => {
                    dispatch(getQuotes({}));
                    dispatch(getQuote({ quoteId: selectedQuoteId }));
                    setIsActioning((prev) => ({ ...prev, reject: false }));
                    dispatch(showAlert({ severity: "success", message: "Quote rejected" }));
                },
            })
        );
    };

    const handleClone = () => {
        setIsActioning((prev) => ({ ...prev, clone: true }));
        dispatch(
            cloneQuote({
                quoteId: selectedQuoteId,
                callback: (data) => {
                    setIsActioning((prev) => ({ ...prev, clone: false }));
                    if (!data) return;
                    dispatch(getQuotes({}));
                    navigate(`/quotes/${data._id}`);
                    dispatch(showAlert({ severity: "success", message: "Quote cloned. You are now viewing the copy." }));
                },
            })
        );
    };

    const isRespondingOnBehalf = isActioning.accept || isActioning.decline;

    const handleRespondOnBehalf = (action) => {
        setOnBehalfNote('');
        setOnBehalfAction(action);
    };

    const handleRespondOnBehalfCancel = () => {
        if (isRespondingOnBehalf) return;
        setOnBehalfAction(null);
        setOnBehalfNote('');
    };

    const submitRespondOnBehalf = () => {
        const quoteId = selectedQuoteId;
        const action = onBehalfAction;
        const note = onBehalfNote.trim();
        if (!note) return;
        setIsActioning((prev) => ({ ...prev, [action]: true }));
        const onDone = (data) => {
            setIsActioning((prev) => ({ ...prev, [action]: false }));
            if (!data) return;
            setOnBehalfAction(null);
            setOnBehalfNote('');
            dispatch(getQuotes({}));
            dispatch(getQuote({ quoteId }));
            dispatch(showAlert({
                severity: "success",
                message: action === 'accept'
                    ? "Quote accepted for the client. The project has been created."
                    : "Quote declined for the client.",
            }));
        };
        dispatch(action === 'accept'
            ? acceptQuote({ quoteId, data: { note }, callback: onDone })
            : declineQuote({ quoteId, data: { note }, callback: onDone }));
    };

    const handlePreviewPdf = () => {
        const previewWindow = window.open('', '_blank');
        setIsActioning((prev) => ({ ...prev, previewPdf: true }));
        dispatch(
            previewQuotePdf({
                quoteId: selectedQuoteId,
                callback: (data) => {
                    setIsActioning((prev) => ({ ...prev, previewPdf: false }));
                    if (!data) {
                        previewWindow?.close();
                        return;
                    }
                    const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
                    if (previewWindow) previewWindow.location.href = url;
                    else window.open(url, '_blank');
                    setTimeout(() => URL.revokeObjectURL(url), 10 * 60 * 1000);
                },
            })
        );
    };

    const handleReopenCancel = () => {
        if (isActioning.reopen) return;
        setReopenDialogOpen(false);
    };

    const handleReopenSubmit = () => {
        const quoteId = selectedQuoteId;
        setIsActioning((prev) => ({ ...prev, reopen: true }));
        dispatch(
            reopenQuote({
                quoteId,
                callback: (data) => {
                    setIsActioning((prev) => ({ ...prev, reopen: false }));
                    if (!data) return;
                    setReopenDialogOpen(false);
                    dispatch(getQuotes({}));
                    dispatch(showAlert({ severity: "success", message: "Quote moved back to Draft" }));
                    if (selectedQuoteIdRef.current !== quoteId) return;
                    dispatch(
                        getQuote({
                            quoteId,
                            callback: (quote) => {
                                if (!quote || selectedQuoteIdRef.current !== quoteId) return;
                                dispatch(setCurrentQuote(quote));
                                startEditing(quote);
                            },
                        })
                    );
                },
            })
        );
    };

    const handleCommentsChanged = (quoteId) => (nextComments) => {
        dispatch(setCurrentQuoteComments({ quoteId, comments: nextComments }));
    };

    const handleRegenerate = () => {
        setRegenerateInput('');
        setRegenerateDialogOpen(true);
    };

    const handleRegenerateCancel = () => {
        if (isActioning.regenerate) return;
        setRegenerateDialogOpen(false);
        setRegenerateInput('');
    };

    const submitRegenerate = ({ options, actionKey, onDone, successMessage }) => {
        const quoteId = selectedQuoteId;
        setIsActioning((prev) => ({ ...prev, [actionKey]: true }));
        dispatch(
            regenerateQuote(quoteId, options, (data) => {
                setIsActioning((prev) => ({ ...prev, [actionKey]: false }));
                if (!data) return;
                dispatch(getQuotes({}));
                dispatch(getQuote({ quoteId }));
                onDone();
                dispatch(showAlert({ severity: "success", message: successMessage }));
            })
        );
    };

    const handleRegenerateSubmit = () => {
        submitRegenerate({
            options: { managerInput: regenerateInput.trim() },
            actionKey: 'regenerate',
            successMessage: "Quote sent back to Pending. The AI is redrafting it now.",
            onDone: () => {
                setRegenerateDialogOpen(false);
                setRegenerateInput('');
            },
        });
    };

    const attachedTemplateId = currentQuote?.quoteTemplate?._id || currentQuote?.quoteTemplate || '';
    const attachedTemplate = attachedTemplateId ? quoteTemplates.find((t) => t._id === attachedTemplateId) || null : null;
    const attachedTemplateLabel = attachedTemplateId
        ? (attachedTemplate ? attachedTemplate.name : "A template is attached, but it is no longer in your template list")
        : "None — the AI drafts this quote from scratch";
    const isDetachingTemplate = !applyTemplateId && !!attachedTemplateId;

    const handleApplyTemplate = () => {
        setApplyTemplateId(attachedTemplateId || '');
        setApplyTemplateOpen(true);
    };

    const handleApplyTemplateCancel = () => {
        if (isActioning.applyTemplate) return;
        setApplyTemplateOpen(false);
        setApplyTemplateId('');
    };

    const handleApplyTemplateSubmit = () => {
        submitRegenerate({
            options: { quoteTemplateId: applyTemplateId || null },
            actionKey: 'applyTemplate',
            successMessage: isDetachingTemplate
                ? "Template removed. The quote is back to Pending and the AI is redrafting it from scratch."
                : "Template applied. The quote has been rebuilt from it and is ready to review in Draft.",
            onDone: () => {
                setApplyTemplateOpen(false);
                setApplyTemplateId('');
            },
        });
    };

    const formatDate = (dateString) => formatDay(dateString) || "-";
    const formatTimestamp = (dateString) => formatDateTime(dateString) || "-";


    const paymentsTotal = editedPayments.reduce((s, p) => s + (parseFloat(p.percentage) || 0), 0);
    const paymentsValid = !editMode || Math.abs(paymentsTotal - 100) < 0.01;
    const lockedRateValid = !editMode || (String(editedLockedRate).trim() !== '' && Number.isFinite(Number(editedLockedRate)) && Number(editedLockedRate) >= 0);

    const canSetProjectBilling = hasPermission("setProjectBilling");
    const canEdit = currentQuote && (currentQuote.status === "Pending" || currentQuote.status === "Draft" || currentQuote.status === "Under Review");
    const canApprove = currentQuote && (currentQuote.status === "Pending" || currentQuote.status === "Draft" || currentQuote.status === "Under Review");
    const canReject = currentQuote && currentQuote.status !== "Accepted" && currentQuote.status !== "Declined" && currentQuote.status !== "Rejected" && currentQuote.status !== "Sent";
    const canRegenerate = currentQuote && (currentQuote.status === "Pending" || currentQuote.status === "Draft" || currentQuote.status === "Under Review");
    const canRespondOnBehalf = currentQuote && currentQuote.status === "Sent";
    const canReopen = currentQuote && currentQuote.status === "Sent";

    const eligibleNotifyUsers = currentQuote?.eligibleNotifyUsers;
    const eligibleNotifyIds = new Set((eligibleNotifyUsers || []).map((u) => String(u._id)));
    const visibleNotifyUsers = (currentQuote?.notifyUsers || []).filter((u) => !eligibleNotifyUsers || eligibleNotifyIds.has(String(u._id || u)));
    const requesterId = currentQuote?.requestedBy?._id || currentQuote?.requestedBy;
    const hasClientRecipient = !eligibleNotifyUsers
        || eligibleNotifyIds.has(String(requesterId))
        || visibleNotifyUsers.length > 0;

    const handleSaveAsTemplate = ({ name, description }) => {
        setSavingTemplate(true);
        dispatch(
            createQuoteTemplateFromQuote(selectedQuoteId, { name, description }, (data) => {
                setSavingTemplate(false);
                if (!data) return;
                setSaveTemplateOpen(false);
            })
        );
    };

    const renderQuoteDetail = () => (
        <DetailPanel>
            <DetailHeader>
                <Typography variant="h6" fontWeight="bold">Quote Detail</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <StatusChip label={currentQuote.status || "Draft"} $status={currentQuote.status || "Draft"} size="small" />
                    {hasPermission("canManageQuotes") && (() => {
                        const hasLineItems = (currentQuote.lineItems || []).length > 0;
                        return (
                            <Tooltip title={hasLineItems
                                ? "Copy this quote's scope into a reusable template for future quotes"
                                : "There is nothing to copy yet. Wait for the AI to draft this quote, or add the line items yourself, then save it as a standard engagement."}>
                                <span>
                                    <Button size="small" variant="outlined" disabled={!hasLineItems} onClick={() => setSaveTemplateOpen(true)}>
                                        Save as template
                                    </Button>
                                </span>
                            </Tooltip>
                        );
                    })()}
                    {hasPermission("canManageQuotes") && (
                        <Tooltip title={editMode ? "Save or cancel your changes before cloning" : "Create a new Draft quote that copies this one, so you can offer the client another option"}>
                            <span>
                                <Button size="small" variant="outlined" disabled={isActioning.clone || editMode} onClick={handleClone}
                                    startIcon={isActioning.clone ? <CircularProgress size={16} color="inherit" /> : undefined}>
                                    Clone
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            </DetailHeader>
            {currentQuote.status === "Accepted" && currentQuote.project && currentQuote.account?.slug && (
                <ProjectLinkNotice severity="success">
                    Project created &rarr;{" "}
                    <Link component="button" onClick={() => navigate(`/account/${currentQuote.account.slug}/project/${currentQuote.project._id || currentQuote.project}`)}>
                        {currentQuote.project.name || "View project"}
                    </Link>
                </ProjectLinkNotice>
            )}
            {currentQuote.status === "Accepted" && currentQuote.depositInvoice && (
                <DepositInvoiceNotice severity="info">
                    Deposit invoice generated &rarr;{" "}
                    <Link component="button" onClick={() => navigate(`/billing/invoices/${currentQuote.depositInvoice._id || currentQuote.depositInvoice}`)}>
                        #{currentQuote.depositInvoice.invoiceNumber || "INVOICE"}
                    </Link>
                    {" "}({currentQuote.depositInvoice.status || "Draft"})
                </DepositInvoiceNotice>
            )}
            {currentQuote.respondedOnBehalf?.user && (
                <OnBehalfNotice severity="info">
                    {currentQuote.status === "Declined" ? "Declined" : "Accepted"} for the client by{" "}
                    <strong>{currentQuote.respondedOnBehalf.user.fullName || currentQuote.respondedOnBehalf.user.email}</strong>
                    {currentQuote.respondedOnBehalf.note ? ` — ${currentQuote.respondedOnBehalf.note}` : ""}
                </OnBehalfNotice>
            )}
            <DetailDivider />

            <RushRow>
                <Typography variant="body2">Rush</Typography>
                {editMode ? (
                    <Tooltip title={canSetProjectBilling ? "Mark this quote as a rush job. The project created on acceptance is marked rush too." : "Changing rush requires permission to set project billing"}>
                        <span>
                            <Switch
                                checked={editedRush}
                                onChange={(e) => setEditedRush(e.target.checked)}
                                disabled={!canSetProjectBilling}
                                inputProps={{ "aria-label": "Rush" }}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <Chip size="small" color={currentQuote.rush ? "error" : "default"} label={currentQuote.rush ? "Yes" : "No"} />
                )}
            </RushRow>

            <MetaGrid>
                <Box>
                    <Typography variant="caption" color="text.secondary">Project Name</Typography>
                    {editMode
                        ? <TextField size="small" fullWidth value={editedName} onChange={(e) => setEditedName(e.target.value)} />
                        : <Typography variant="body1">{currentQuote.name || "—"}</Typography>
                    }
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Project Type</Typography>
                    <Typography variant="body1">{currentQuote.projectType || "-"}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Requested By</Typography>
                    <Typography variant="body1">{currentQuote.requestedBy?.fullName || currentQuote.requestedBy?.email || '—'}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body1">{formatTimestamp(currentQuote.createdAt)}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Total Hours</Typography>
                    <Typography variant="body1">{currentQuote.totalHours != null ? currentQuote.totalHours : "-"}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Quote Template</Typography>
                    <Typography variant="body1">{attachedTemplateLabel}</Typography>
                </Box>
                {editMode && (
                    <>
                        <Box>
                            <AppDatePicker label="Due Date" value={editedDueDate}
                                onChange={(v) => setEditedDueDate(v)} />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Department</Typography>
                            <FormControl size="small" fullWidth>
                                <Select value={editedDepartment} onChange={(e) => setEditedDepartment(e.target.value)} displayEmpty>
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {departments.map((d) => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Skill</Typography>
                            <FormControl size="small" fullWidth>
                                <Select value={editedSkill} onChange={(e) => setEditedSkill(e.target.value)} displayEmpty>
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {selectableSkills(skills, editedSkill).map((s) => <MenuItem key={s._id} value={s._id}>{skillOptionLabel(s)}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Billing Type</Typography>
                            <FormControl size="small" fullWidth>
                                <Select value={editedBillingType} onChange={(e) => setEditedBillingType(e.target.value)}>
                                    {['Retainer', 'Hourly', 'Fixed Price', 'Internal', 'Non Billable'].map((opt) => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Rate ($/hr)</Typography>
                            <TextField type="number" size="small" fullWidth value={editedLockedRate}
                                onChange={(e) => setEditedLockedRate(e.target.value)}
                                error={!lockedRateValid}
                                helperText={!lockedRateValid ? "Enter a rate of 0 or more" : undefined}
                                inputProps={{ min: 0, step: "0.01" }} />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Days Before Start</Typography>
                            <TextField type="number" size="small" fullWidth value={editedDaysBeforeStart}
                                onChange={(e) => setEditedDaysBeforeStart(parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0 }} />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Extra Days (after last task)</Typography>
                            <TextField type="number" size="small" fullWidth value={editedExtraDays}
                                onChange={(e) => setEditedExtraDays(parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0 }} />
                        </Box>
                    </>
                )}
                {!editMode && (
                    <>
                        {currentQuote.dueDate && <Box><Typography variant="caption" color="text.secondary">Due Date</Typography><Typography variant="body1">{formatDate(currentQuote.dueDate)}</Typography></Box>}
                        {currentQuote.department && <Box><Typography variant="caption" color="text.secondary">Department</Typography><Typography variant="body1">{currentQuote.department?.name || currentQuote.department}</Typography></Box>}
                        {currentQuote.skill && <Box><Typography variant="caption" color="text.secondary">Skill</Typography><Typography variant="body1">{currentQuote.skill?.name || currentQuote.skill}</Typography></Box>}
                        {currentQuote.billingType && <Box><Typography variant="caption" color="text.secondary">Billing Type</Typography><Typography variant="body1">{currentQuote.billingType}</Typography></Box>}
                        {(currentQuote.daysBeforeStart > 0) && (
                            <Box><Typography variant="caption" color="text.secondary">Days Before Start</Typography>
                            <Typography variant="body1">{currentQuote.daysBeforeStart}</Typography></Box>
                        )}
                        {(currentQuote.extraDays > 0) && (
                            <Box><Typography variant="caption" color="text.secondary">Extra Days</Typography>
                            <Typography variant="body1">{currentQuote.extraDays}</Typography></Box>
                        )}
                    </>
                )}
            </MetaGrid>

            <NotifyBox>
                {editMode ? (
                    <Autocomplete
                        multiple
                        options={eligibleNotifyUsers || []}
                        loading={!eligibleNotifyUsers}
                        value={editedNotifyUsers}
                        onChange={(_, v) => setEditedNotifyUsers(v)}
                        getOptionLabel={(o) => o?.fullName || o?.email || ""}
                        isOptionEqualToValue={(o, v) => String(o._id) === String(v._id)}
                        filterSelectedOptions
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                label="Notify"
                                placeholder="Choose client users"
                                helperText="Client users who get emails about this quote, such as when it is sent for review. A client who requested the quote is always included."
                            />
                        )}
                    />
                ) : (
                    <>
                        <Typography variant="caption" color="text.secondary">Notify</Typography>
                        <Typography variant="body1">
                            {visibleNotifyUsers.length > 0
                                ? visibleNotifyUsers.map((u) => u.fullName || u.email).join(", ")
                                : "—"}
                        </Typography>
                    </>
                )}
            </NotifyBox>

            <SectionLabel variant="caption" color="text.secondary">DESCRIPTION</SectionLabel>
            <DescriptionBox>
                {editMode
                    ? <MarkdownEditor
                          key={`desc-edit-${selectedQuoteId}`}
                          initialContent={editedDescription || currentQuote.description || null}
                          onChange={(content) => setEditedDescription(content)}
                      />
                    : <MarkdownDisplay content={currentQuote.description || null} />
                }
            </DescriptionBox>

            {Array.isArray(currentQuote.attachments) && currentQuote.attachments.filter((a) => !a.isDeleted).length > 0 && (
                <>
                    <SectionLabel variant="caption" color="text.secondary">ATTACHMENTS</SectionLabel>
                    <Box mb={2} display="flex" flexDirection="column" gap={0.5}>
                        {currentQuote.attachments.filter((a) => !a.isDeleted).map((att) => (
                            <Box key={att._id} display="flex" alignItems="center" gap={1}>
                                <Button
                                    size="small"
                                    variant="text"
                                    href={`/api/attachment/${att._id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {att.name}
                                </Button>
                                <Typography variant="caption" color="text.secondary">
                                    {att.mimeType} · {Math.round((att.size || 0) / 1024)} KB
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </>
            )}

            {currentQuote.quotePdf && (
                <Box mb={2}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                        href={`/api/attachment/${currentQuote.quotePdf._id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Download quote PDF
                    </Button>
                </Box>
            )}

            <SectionLabel variant="caption" color="text.secondary">NOTES (CLIENT-FACING)</SectionLabel>
            <Box mb={2}>
                {editMode
                    ? <TextField fullWidth multiline rows={3} size="small" value={editedNotes}
                        onChange={(e) => setEditedNotes(e.target.value)}
                        placeholder="Client-facing notes, shown to the client after the quote is sent. Markdown supported." />
                    : (currentQuote.notes
                        ? <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>{currentQuote.notes}</Markdown>
                        : <Typography variant="body2">—</Typography>)
                }
            </Box>

            {renderScopeList('ASSUMPTIONS', 'Conditions the scope relies on. Shown to the client after the quote is sent.', editMode ? editedAssumptions : (currentQuote.assumptions || []), setEditedAssumptions)}
            {renderScopeList('INCLUSIONS', 'What is explicitly in scope. Shown to the client after the quote is sent.', editMode ? editedInclusions : (currentQuote.inclusions || []), setEditedInclusions)}
            {renderScopeList('EXCLUSIONS', 'What is explicitly out of scope. Shown to the client after the quote is sent.', editMode ? editedExclusions : (currentQuote.exclusions || []), setEditedExclusions)}

            <SectionLabel variant="caption" color="text.secondary">MANAGER NOTES (INTERNAL — NOT SHOWN TO CLIENT)</SectionLabel>
            <Box mb={2}>
                {editMode
                    ? <TextField fullWidth multiline rows={4} size="small" value={editedManagerNotes}
                        onChange={(e) => setEditedManagerNotes(e.target.value)}
                        placeholder="Internal briefing for the reviewing manager. Never shown to the client. Markdown supported." />
                    : (currentQuote.managerNotes
                        ? <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>{currentQuote.managerNotes}</Markdown>
                        : <Typography variant="body2">—</Typography>)
                }
            </Box>

            <SectionLabelTight variant="caption" color="text.secondary">LINE ITEMS</SectionLabelTight>
            <LineItemsHelperText variant="caption">
                The title is the short label the client sees on the quote, and it becomes the task name when the client accepts. The description holds the full detail and becomes the task description.
            </LineItemsHelperText>
            <LineItemsTableWrapper>
            <LineItemsTable size="small">
                <TableHead>
                    <TableRow>
                        <TitleHeaderCell>Title</TitleHeaderCell>
                        <DescriptionHeaderCell>Description</DescriptionHeaderCell>
                        <TableCell>Work Type</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell>Task</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(!currentQuote.lineItems || currentQuote.lineItems.length === 0) && !editMode && (
                        <LineItemRow>
                            <TableCell colSpan={6} align="center">
                                <Typography variant="body2" color="text.secondary">No line items</Typography>
                            </TableCell>
                        </LineItemRow>
                    )}
                    {editMode
                        ? editedLineItems.map((item, index) => (
                              <LineItemRow key={index}>
                                  <TableCell>
                                      <TextField
                                          size="small"
                                          fullWidth
                                          placeholder="Short title"
                                          value={item.title || ''}
                                          onChange={(e) => handleLineItemTitleChange(index, e.target.value)}
                                      />
                                  </TableCell>
                                  <TableCell>
                                      <TextField
                                          size="small"
                                          fullWidth
                                          multiline
                                          minRows={2}
                                          placeholder="What this line covers"
                                          value={item.description || ''}
                                          onChange={(e) => handleLineItemDescriptionChange(index, e.target.value)}
                                      />
                                  </TableCell>
                                  <TableCell>
                                      <FormControl size="small" style={{ minWidth: 140 }}>
                                          <Select
                                              value={item.workType || ''}
                                              displayEmpty
                                              onChange={(e) => handleLineItemWorkTypeChange(index, e.target.value || null)}
                                          >
                                              <MenuItem value=""><em>None</em></MenuItem>
                                              {selectableSkills(skills, item.workType).map((s) => (
                                                  <MenuItem key={s._id} value={s._id}>{skillOptionLabel(s)}</MenuItem>
                                              ))}
                                          </Select>
                                      </FormControl>
                                  </TableCell>
                                  <TableCell align="right">
                                      <HoursField
                                          type="number"
                                          size="small"
                                          value={item.hours}
                                          inputProps={{ min: 0 }}
                                          onChange={(e) => handleLineItemHoursChange(index, e.target.value)}
                                      />
                                  </TableCell>
                                  <TableCell>
                                      <Tooltip title="Create task on project">
                                          <Checkbox
                                              size="small"
                                              checked={!!item.isActionable}
                                              onChange={(e) => handleLineItemActionableChange(index, e.target.checked)}
                                          />
                                      </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">
                                      <Button size="small" color="error" onClick={() => handleDeleteLineItem(index)}>
                                          Remove
                                      </Button>
                                  </TableCell>
                              </LineItemRow>
                          ))
                        : (currentQuote.lineItems || []).map((item, index) => (
                              <LineItemRow key={index}>
                                  <TableCell>{item.title || '—'}</TableCell>
                                  <TableCell>{item.description || '—'}</TableCell>
                                  <TableCell>{item.workType?.name || '—'}</TableCell>
                                  <TableCell align="right">{item.hours}</TableCell>
                                  <TableCell>{item.isActionable ? "✓" : ""}</TableCell>
                                  <TableCell />
                              </LineItemRow>
                          ))}
                </TableBody>
            </LineItemsTable>
            </LineItemsTableWrapper>

            {editMode && (
                <Box mt={1} mb={1}>
                    <Button variant="outlined" size="small" onClick={handleAddLineItem}>
                        + Add Line Item
                    </Button>
                </Box>
            )}

            {((localContingencyHours ?? currentQuote.contingencyHours) > 0 || currentQuote.contingencyRate != null) && (
                <ContingencyRow>
                    <Typography variant="body2" color="text.secondary">
                        Contingency ({currentQuote.contingencyRate != null ? `${Math.round(currentQuote.contingencyRate * 100)}%` : "—"})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {localContingencyHours ?? currentQuote.contingencyHours} hrs
                    </Typography>
                </ContingencyRow>
            )}

            {editMode && (
                <DiscountRow>
                    <DiscountFormControl size="small">
                        <InputLabel>Discount Type</InputLabel>
                        <Select value={editedDiscountType} label="Discount Type" onChange={(e) => setEditedDiscountType(e.target.value)}>
                            <MenuItem value=""><em>None</em></MenuItem>
                            <MenuItem value="percent">Percent</MenuItem>
                            <MenuItem value="fixed">Fixed</MenuItem>
                        </Select>
                    </DiscountFormControl>
                    {editedDiscountType && (
                        <DiscountValueField size="small" type="number" label="Discount Value" value={editedDiscountValue}
                            onChange={(e) => setEditedDiscountValue(parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0 }} />
                    )}
                </DiscountRow>
            )}

            {(editMode ? lockedRateValid : currentQuote.lockedRate != null) && (
                <PriceSummaryBox>
                    {(() => {
                        const liveLineItems = editMode ? editedLineItems : (currentQuote.lineItems || []);
                        const subtotalHours = liveLineItems.reduce((s, i) => s + (parseFloat(i.hours) || 0), 0);
                        const contingencyRate = currentQuote.contingencyRate || 0;
                        const contingencyHours = editMode
                            ? Math.round(subtotalHours * contingencyRate)
                            : (localContingencyHours ?? currentQuote.contingencyHours ?? 0);
                        const totalHours = subtotalHours + contingencyHours;
                        const rate = (editMode ? Number(editedLockedRate) : currentQuote.lockedRate) || 0;
                        const totalGross = totalHours * rate;
                        const discountType = editMode ? editedDiscountType : currentQuote.discountType;
                        const discountValue = editMode ? editedDiscountValue : currentQuote.discountValue;
                        let discountAmount = 0;
                        if (discountValue > 0) {
                            discountAmount = discountType === 'percent'
                                ? totalGross * (discountValue / 100)
                                : discountValue;
                        }
                        const totalPrice = totalGross - discountAmount;
                        const fmt = (n) => `$${n.toFixed(2)}`;
                        return (
                            <>
                                <PriceSummaryRow>
                                    <Typography variant="body2" color="text.secondary">Subtotal ({subtotalHours} hrs × ${rate}/hr)</Typography>
                                    <Typography variant="body2">{fmt(subtotalHours * rate)}</Typography>
                                </PriceSummaryRow>
                                {contingencyHours > 0 && (
                                    <PriceSummaryRow>
                                        <Typography variant="body2" color="text.secondary">Contingency ({contingencyHours} hrs × ${rate}/hr)</Typography>
                                        <Typography variant="body2">{fmt(contingencyHours * rate)}</Typography>
                                    </PriceSummaryRow>
                                )}
                                <PriceSummaryRow>
                                    <Typography variant="body2" color="text.secondary">Total Hours</Typography>
                                    <Typography variant="body2">{totalHours} hrs</Typography>
                                </PriceSummaryRow>
                                {discountAmount > 0 && (
                                    <PriceSummaryRow>
                                        <Typography variant="body2" color="text.secondary">
                                            Discount ({discountType === 'percent' ? `${discountValue}%` : 'fixed'})
                                        </Typography>
                                        <Typography variant="body2" color="error.main">−{fmt(discountAmount)}</Typography>
                                    </PriceSummaryRow>
                                )}
                                <PriceSummaryRow>
                                    <Typography variant="body2" fontWeight="bold">Total Price</Typography>
                                    <Typography variant="body2" fontWeight="bold">{fmt(totalPrice)}</Typography>
                                </PriceSummaryRow>
                            </>
                        );
                    })()}
                </PriceSummaryBox>
            )}

            {(editMode ? editedBillingType : (currentQuote?.billingType || 'Fixed Price')) === 'Fixed Price' && <PaymentEditorBox>
                <SectionLabelTight variant="caption" color="text.secondary">PAYMENT SCHEDULE</SectionLabelTight>
                {editMode ? (
                    <>
                        {editedPayments.map((payment, idx) => (
                            <PaymentRow key={idx}>
                                <Typography variant="body2" color="text.secondary" style={{ minWidth: 24 }}>
                                    {idx + 1}.
                                </Typography>
                                <NarrowField
                                    type="number"
                                    size="small"
                                    label="%"
                                    value={payment.percentage}
                                    inputProps={{ min: 0, max: 100 }}
                                    onChange={(e) => {
                                        const updated = editedPayments.map((p, i) =>
                                            i === idx ? { ...p, percentage: parseFloat(e.target.value) || 0 } : p
                                        );
                                        setEditedPayments(updated);
                                    }}
                                />
                                <FormControl size="small" style={{ minWidth: 160 }}>
                                    <Select
                                        value={payment.relativeDate || ''}
                                        displayEmpty
                                        onChange={(e) => {
                                            const updated = editedPayments.map((p, i) =>
                                                i === idx ? { ...p, relativeDate: e.target.value || null, date: e.target.value ? null : p.date } : p
                                            );
                                            setEditedPayments(updated);
                                        }}
                                    >
                                        <MenuItem value=""><em>Specific Date</em></MenuItem>
                                        <MenuItem value="start">Project Start Date</MenuItem>
                                        <MenuItem value="due">Project Due Date</MenuItem>
                                    </Select>
                                </FormControl>
                                {!payment.relativeDate && (
                                    <AppDatePicker
                                        size="small"
                                        label="Date"
                                        value={formatDay(payment.date)}
                                        onChange={(v) => {
                                            const updated = editedPayments.map((p, i) =>
                                                i === idx ? { ...p, date: v } : p
                                            );
                                            setEditedPayments(updated);
                                        }}
                                    />
                                )}
                                <Button size="small" color="error" disabled={editedPayments.length <= 1}
                                    onClick={() => setEditedPayments((prev) => prev.filter((_, i) => i !== idx))}>
                                    Remove
                                </Button>
                            </PaymentRow>
                        ))}
                        {!paymentsValid && (
                            <Typography variant="caption" color="error">
                                Percentages must sum to 100% (currently {paymentsTotal.toFixed(1)}%)
                            </Typography>
                        )}
                        <Button size="small" variant="outlined" onClick={() =>
                            setEditedPayments((prev) => [...prev, { paymentNumber: prev.length + 1, percentage: 0, date: '', status: 'Pending' }])
                        }>
                            + Add Payment
                        </Button>
                    </>
                ) : (
                    (currentQuote.payments && currentQuote.payments.length > 0) ? (
                        currentQuote.payments.map((p, idx) => (
                            <Typography key={idx} variant="body2">
                                Payment {idx + 1}: {p.percentage}% — {
                                    p.relativeDate === 'start' ? 'Project Start Date' :
                                    p.relativeDate === 'due' ? 'Project Due Date' :
                                    p.date ? formatDay(p.date) : '—'
                                }
                            </Typography>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">No payment schedule set</Typography>
                    )
                )}
            </PaymentEditorBox>}

            <ActionButtonsRow>
                {canEdit && !editMode && (
                    <Button variant="outlined" startIcon={<Edit />} onClick={handleEditLineItems}>
                        Edit Quote
                    </Button>
                )}
                {editMode && (
                    <>
                        <Button variant="contained" onClick={handleSaveChanges} disabled={!lockedRateValid}>
                            Save Changes
                        </Button>
                        <Button variant="outlined" onClick={handleCancelEdit}>
                            Cancel Edit
                        </Button>
                    </>
                )}
                {canApprove && !editMode && (() => {
                    const actionableCount = (currentQuote?.lineItems || []).filter((i) => i.isActionable).length;
                    const hasActionable = actionableCount > 0;
                    const sendDisabled = isActioning.approve || !paymentsValid || !hasActionable || !hasClientRecipient;
                    const sendTooltip = !hasActionable
                        ? 'Quote must have at least one actionable line item before sending'
                        : (!paymentsValid
                            ? `Payment percentages must sum to 100% (currently ${paymentsTotal.toFixed(1)}%)`
                            : (!hasClientRecipient
                                ? 'Nobody at the client would receive this quote. Edit the quote and choose who to notify.'
                                : 'Send this quote to the client'));
                    return (
                        <Tooltip title={sendTooltip}>
                            <span>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={isActioning.approve ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                                    disabled={sendDisabled}
                                    onClick={handleApprove}
                                >
                                    Send
                                </Button>
                            </span>
                        </Tooltip>
                    );
                })()}
                {canApprove && !editMode && (
                    <Button
                        variant="outlined"
                        startIcon={isActioning.previewPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
                        disabled={isActioning.previewPdf}
                        onClick={handlePreviewPdf}
                    >
                        Preview PDF
                    </Button>
                )}
                {canReopen && !editMode && (
                    <Tooltip title="Move this quote back to Draft so you can change it and send it again">
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={isActioning.reopen ? <CircularProgress size={16} color="inherit" /> : <Edit />}
                                disabled={isActioning.reopen}
                                onClick={() => setReopenDialogOpen(true)}
                            >
                                Edit Quote
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {canRespondOnBehalf && !editMode && (
                    <Tooltip title="Record the client's acceptance yourself, when they approved outside Pollux">
                        <span>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={isActioning.accept ? <CircularProgress size={16} color="inherit" /> : <HowToReg />}
                                disabled={isRespondingOnBehalf}
                                onClick={() => handleRespondOnBehalf('accept')}
                            >
                                Accept for client
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {canRespondOnBehalf && !editMode && (
                    <Tooltip title="Record the client's decline yourself, when they turned it down outside Pollux">
                        <span>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={isActioning.decline ? <CircularProgress size={16} color="inherit" /> : <Block />}
                                disabled={isRespondingOnBehalf}
                                onClick={() => handleRespondOnBehalf('decline')}
                            >
                                Decline for client
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {canReject && !editMode && (
                    <Tooltip title="Reject this quote">
                        <span>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={isActioning.reject ? <CircularProgress size={16} color="inherit" /> : <Cancel />}
                                disabled={isActioning.reject}
                                onClick={handleReject}
                            >
                                Reject
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {canRegenerate && !editMode && (
                    <Tooltip title={attachedTemplateId
                        ? `This quote follows "${attachedTemplate?.name || "an attached template"}". Swap it for another standard engagement, or remove it so the AI drafts from scratch.`
                        : "Redraft this quote from one of your standard engagements. The quote goes back to Pending for the AI to rewrite."}>
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={isActioning.applyTemplate ? <CircularProgress size={16} color="inherit" /> : <ContentPaste />}
                                disabled={isActioning.applyTemplate}
                                onClick={handleApplyTemplate}
                            >
                                {attachedTemplateId ? "Change template" : "Apply template"}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {canRegenerate && !editMode && (
                    <Tooltip title={attachedTemplateId ? "Redraft with AI, still following the attached template" : "Regenerate with AI"}>
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={isActioning.regenerate ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                                disabled={isActioning.regenerate}
                                onClick={handleRegenerate}
                            >
                                Regenerate
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </ActionButtonsRow>

            {(currentQuote.status === "Sent" || currentQuote.comments?.length > 0) && (
                <>
                    <DetailDivider />
                    <SectionLabel variant="caption" color="text.secondary">COMMENTS</SectionLabel>
                    <CommentThread
                        key={currentQuote._id}
                        parentType="quote"
                        parentId={currentQuote._id}
                        comments={currentQuote.comments || []}
                        onChanged={handleCommentsChanged(currentQuote._id)}
                        readOnly={currentQuote.status !== "Sent"}
                    />
                </>
            )}
        </DetailPanel>
    );

    const isDetailVisible = (quoteId) => selectedQuoteId === quoteId && currentQuote && currentQuote._id === quoteId;

    const renderExpandedDetail = (quoteId) => (
        isDetailVisible(quoteId)
            ? renderQuoteDetail()
            : (
                <DetailLoadingBox>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading quote detail…</Typography>
                </DetailLoadingBox>
            )
    );

    return (
        <PageContainer maxWidth="lg">
            <PageHeader
                title="Quotes"
                subtitle="Manage all client quotes and proposals"
                actions={
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setNewQuoteOpen(true)}
                    >
                        New Quote
                    </Button>
                }
            />

            <QuoteRequestDialog
                open={newQuoteOpen}
                onClose={() => setNewQuoteOpen(false)}
                onCreated={() => dispatch(getQuotes({}))}
            />

            <TabContext value={tabValue}>
                <TabsWrapper>
                    <TabList onChange={handleTabChange} aria-label="manager quote tabs">
                        <Tab label={tabBadgeLabel('Active', 'active')} value="active" />
                        <Tab label={tabBadgeLabel('Sent', 'sent')} value="sent" />
                        <Tab label={tabBadgeLabel('Approved', 'approved')} value="approved" />
                        <Tab label={tabBadgeLabel('Rejected', 'rejected')} value="rejected" />
                        <Tab label="All" value="all" />
                    </TabList>
                </TabsWrapper>

                <TableWrapper>
                <Paper>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Account</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Project Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Total Hours</TableCell>
                                <TableCell>Date</TableCell>
                                <ChevronCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pagedQuotes.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            No quotes found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {pagedQuotes.map((quote) => (
                                <React.Fragment key={quote._id}>
                                    <QuoteRow
                                        $selected={selectedQuoteId === quote._id}
                                        onClick={() => handleRowClick(quote._id)}
                                    >
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <TableChart fontSize="small" color="action" />
                                                {quote.account?.name || "-"}
                                            </Box>
                                        </TableCell>
                                        <TableCell><MarkdownDisplay content={quote.description || null} singleLine /></TableCell>
                                        <TableCell>{quote.projectType || "-"}</TableCell>
                                        <TableCell>
                                            <StatusChip label={quote.status || "Draft"} $status={quote.status || "Draft"} size="small" />
                                        </TableCell>
                                        <TableCell align="right">{quote.totalHours != null ? quote.totalHours : "-"}</TableCell>
                                        <TableCell>{formatTimestamp(quote.createdAt)}</TableCell>
                                        <ChevronCell align="right">
                                            <Tooltip title={selectedQuoteId === quote._id ? "Hide quote detail" : "Show quote detail"}>
                                                {selectedQuoteId === quote._id
                                                    ? <ExpandLess color="primary" fontSize="small" />
                                                    : <ExpandMore color="action" fontSize="small" />}
                                            </Tooltip>
                                        </ChevronCell>
                                    </QuoteRow>
                                    <TableRow>
                                        <DetailExpansionCell colSpan={7}>
                                            <Collapse in={!isMobile && selectedQuoteId === quote._id} unmountOnExit>
                                                {renderExpandedDetail(quote._id)}
                                            </Collapse>
                                        </DetailExpansionCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
                </TableWrapper>

                <MobileCardList>
                    {pagedQuotes.length === 0 && (
                        <Typography variant="body2" color="text.secondary" align="center">
                            No quotes found
                        </Typography>
                    )}
                    {pagedQuotes.map((quote) => (
                        <QuoteCard
                            key={quote._id}
                            elevation={1}
                            $selected={selectedQuoteId === quote._id}
                            onClick={() => handleRowClick(quote._id)}
                        >
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                {quote.name || quote.description || "—"}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                <TableChart fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    {quote.account?.name || "—"}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                                <StatusChip label={quote.status || "Draft"} $status={quote.status || "Draft"} size="small" />
                                {quote.projectType && (
                                    <Typography variant="caption" color="text.secondary">
                                        {quote.projectType}
                                    </Typography>
                                )}
                                {quote.totalHours != null && (
                                    <Typography variant="caption" color="text.secondary">
                                        {quote.totalHours} hrs
                                    </Typography>
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {formatTimestamp(quote.updatedAt || quote.createdAt)}
                            </Typography>
                            <Collapse in={isMobile && selectedQuoteId === quote._id} unmountOnExit>
                                <MobileDetailPanel onClick={(e) => e.stopPropagation()}>
                                    {renderExpandedDetail(quote._id)}
                                </MobileDetailPanel>
                            </Collapse>
                        </QuoteCard>
                    ))}
                </MobileCardList>

                {totalPages > 1 && (
                    <PaginationBox>
                        <Pagination count={totalPages} page={currentPage} onChange={(_, p) => setCurrentPage(p)} />
                    </PaginationBox>
                )}
            </TabContext>
            <Dialog open={!!onBehalfAction} onClose={handleRespondOnBehalfCancel} fullWidth maxWidth="sm">
                <DialogTitle>
                    {onBehalfAction === 'accept' ? "Accept this quote for the client" : "Decline this quote for the client"}
                </DialogTitle>
                <DialogContent>
                    <DialogHelperText variant="body2">
                        {onBehalfAction === 'accept'
                            ? "Recording the acceptance creates the project, its tasks and the deposit invoice, exactly as the client accepting would."
                            : "Recording the decline closes this quote as the client's decision. No project is created."}
                    </DialogHelperText>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={8}
                        required
                        label="How did the client authorize this?"
                        placeholder="e.g. Approved by email from Taso on 15 Sep"
                        value={onBehalfNote}
                        onChange={(e) => setOnBehalfNote(e.target.value)}
                        disabled={isRespondingOnBehalf}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRespondOnBehalfCancel} disabled={isRespondingOnBehalf}>Cancel</Button>
                    <Tooltip title={onBehalfNote.trim() ? "" : "Add a note recording the client's authorization first"}>
                        <span>
                            <Button
                                variant="contained"
                                color={onBehalfAction === 'accept' ? "success" : "error"}
                                onClick={submitRespondOnBehalf}
                                disabled={isRespondingOnBehalf || !onBehalfNote.trim()}
                                startIcon={isRespondingOnBehalf
                                    ? <CircularProgress size={16} color="inherit" />
                                    : (onBehalfAction === 'accept' ? <HowToReg /> : <Block />)}
                            >
                                {onBehalfAction === 'accept' ? "Accept for client" : "Decline for client"}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            <Dialog open={reopenDialogOpen} onClose={handleReopenCancel} fullWidth maxWidth="xs">
                <DialogTitle>Edit this sent quote?</DialogTitle>
                <DialogContent>
                    <DialogHelperText variant="body2">
                        This moves the quote back to Draft so you can change it and send it again.
                    </DialogHelperText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleReopenCancel} disabled={isActioning.reopen}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleReopenSubmit}
                        disabled={isActioning.reopen}
                        startIcon={isActioning.reopen ? <CircularProgress size={16} color="inherit" /> : <Edit />}
                    >
                        Move to Draft
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={regenerateDialogOpen} onClose={handleRegenerateCancel} fullWidth maxWidth="sm">
                <DialogTitle>Regenerate quote with AI</DialogTitle>
                <DialogContent>
                    <DialogHelperText variant="body2">
                        This sends the quote back to Pending and the AI writes a fresh draft, replacing the current one. Optionally give it guidance on what to change. Examples: "make it cheaper", "drop the QA line", "this is a rush — tighten the timeline", "the client already has a brand kit, skip discovery". Leave blank to redraft from the original description only.
                    </DialogHelperText>
                    <DialogHelperText variant="body2">
                        {attachedTemplateId
                            ? `This redraft will follow the template "${attachedTemplate?.name || "attached to this quote"}". To draft without it, close this and use Change template instead.`
                            : "No template is attached, so the AI drafts from scratch."}
                    </DialogHelperText>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={10}
                        value={regenerateInput}
                        onChange={(e) => setRegenerateInput(e.target.value)}
                        placeholder="Guidance for the AI (optional)"
                        disabled={isActioning.regenerate}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRegenerateCancel} disabled={isActioning.regenerate}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleRegenerateSubmit}
                        disabled={isActioning.regenerate}
                        startIcon={isActioning.regenerate ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                    >
                        Regenerate
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={applyTemplateOpen} onClose={handleApplyTemplateCancel} fullWidth maxWidth="sm">
                <DialogTitle>{attachedTemplateId ? "Change this quote's template" : "Apply a quote template"}</DialogTitle>
                <DialogContent>
                    <DialogHelperText variant="body2">
                        A quote template is one of your standard engagements — the same tasks, hours and prices every time. Pick the one that matches this request and the quote is rebuilt from it straight away. Pick <strong>No template</strong> to let the AI draft from scratch instead.
                    </DialogHelperText>
                    <DialogHelperText variant="body2">
                        Currently following: <strong>{attachedTemplateLabel}</strong>
                    </DialogHelperText>
                    <QuoteTemplatePicker
                        value={applyTemplateId}
                        onChange={(id) => setApplyTemplateId(id)}
                        disabled={isActioning.applyTemplate}
                        helperText="Only active templates are listed, plus the one already attached to this quote."
                    />
                    <ApplyTemplateWarning severity="warning">
                        {isDetachingTemplate
                            ? <>This sends the quote back to <strong>Pending</strong> and the AI rewrites the draft from the client's original request, with no template. The current draft is replaced.</>
                            : <>This replaces the current line items, scope lists and billing settings with the template's, and puts the quote in <strong>Draft</strong>.</>}
                    </ApplyTemplateWarning>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleApplyTemplateCancel} disabled={isActioning.applyTemplate}>Cancel</Button>
                    <Tooltip title={!applyTemplateId && !attachedTemplateId ? "Pick a template first. There is no template attached to remove." : ""}>
                        <span>
                            <Button
                                variant="contained"
                                onClick={handleApplyTemplateSubmit}
                                disabled={(!applyTemplateId && !attachedTemplateId) || isActioning.applyTemplate}
                                startIcon={isActioning.applyTemplate ? <CircularProgress size={16} color="inherit" /> : <ContentPaste />}
                            >
                                {isDetachingTemplate ? "Remove template and redraft" : "Apply template"}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            <SaveQuoteAsTemplateDialog
                open={saveTemplateOpen}
                onClose={() => setSaveTemplateOpen(false)}
                onSave={handleSaveAsTemplate}
                saving={savingTemplate}
                quoteName={currentQuote?.name || ""}
            />
        </PageContainer>
    );
};

export default Quotes;
